import { useState, useEffect } from 'react'
import { X, ShieldOff } from 'lucide-react'
import { lotesService } from '../../services/lotesService'
import toast from 'react-hot-toast'

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Modal "Aplicar Isenção" — sugere, pro leilão inteiro, quais vendedores já
 * se qualificam pra isenção de comissão de defesa (% defendido abaixo do
 * limite configurado em Configurações > Isenções Defesa) e aplica em lote,
 * mediante confirmação, via o mesmo endpoint isentar-defesa de sempre.
 *
 * Props:
 * - organizadorId, leilaoId: int
 * - onClose: () => void
 * - onSaved: () => void
 */
export default function AplicarIsencaoModal({ organizadorId, leilaoId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [candidatos, setCandidatos] = useState([])
  const [selecionados, setSelecionados] = useState({})
  const [erro, setErro] = useState('')

  useEffect(() => {
    let ativo = true
    setLoading(true)
    lotesService.candidatosIsencaoDefesa(organizadorId, leilaoId)
      .then(lista => {
        if (!ativo) return
        setCandidatos(lista)
        setSelecionados(Object.fromEntries(lista.map(c => [c.vendedor_id, true])))
      })
      .catch(err => { if (ativo) setErro(err.response?.data?.detail || 'Erro ao buscar candidatos') })
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false }
  }, [organizadorId, leilaoId])

  const candidatosSelecionados = candidatos.filter(c => selecionados[c.vendedor_id])
  const totalIsento = candidatosSelecionados.reduce((s, c) => s + (c.valor_total_isento_estimado || 0), 0)

  const handleConfirmar = async () => {
    const loteIds = candidatosSelecionados.flatMap(c => c.lote_ids)
    if (loteIds.length === 0) return
    setSaving(true)
    try {
      await lotesService.isentarDefesa(organizadorId, loteIds)
      toast.success(`Isenção aplicada em ${candidatosSelecionados.length} vendedor(es)`)
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-red-100">
          <div className="flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-red-600" />
            <h2 className="text-base font-semibold text-gray-800">Aplicar Isenção de Defesa</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-gray-500">Calculando candidatos...</p>
          ) : erro ? (
            <p className="text-sm text-red-600">{erro}</p>
          ) : candidatos.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhum vendedor elegível no momento — ninguém está abaixo do percentual configurado, ou já foram isentos.</p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Vendedores que defenderam (em valor) menos do que o limite configurado — desmarque quem não quiser isentar agora.
              </p>
              <div className="space-y-1.5 border border-gray-100 rounded-lg p-2">
                {candidatos.map(c => (
                  <label key={c.vendedor_id} className="flex items-center justify-between py-1.5 px-2 rounded text-sm hover:bg-gray-50 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={!!selecionados[c.vendedor_id]}
                        onChange={e => setSelecionados(s => ({ ...s, [c.vendedor_id]: e.target.checked }))} />
                      <span className="font-medium text-gray-800">{c.vendedor_nome}</span>
                      <span className="text-sm font-semibold text-gray-800">defendeu {c.pct_defendido.toFixed(2)}%</span>
                    </div>
                    <span className="font-semibold text-red-600">{fmt(c.valor_total_isento_estimado)}</span>
                  </label>
                ))}
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-red-700">Total que será isento:</span>
                <span className="font-bold text-red-900">{fmt(totalIsento)}</span>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
            Cancelar
          </button>
          <button onClick={handleConfirmar} disabled={saving || candidatosSelecionados.length === 0}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
            {saving ? 'Processando...' : `Isentar ${candidatosSelecionados.length} vendedor(es)`}
          </button>
        </div>
      </div>
    </div>
  )
}
